import { useQuery } from "@tanstack/react-query";

import { fetchApi } from "@/lib/api";

import type { ContactEmail, ContactTranscript } from "./types";

// URL-driven detail hooks: the caller passes the already-built transcript / email
// URL (lead- or person-scoped). The URL is the cache key, so a touch's detail is
// cached distinctly per surface. Fetched lazily — only when a row is expanded —
// since the email body is a per-message GCS round-trip on the backend.

export function useContactDetailTranscript(url: string, { enabled }: { enabled: boolean }) {
  return useQuery<ContactTranscript>({
    queryKey: ["contact-transcript", url],
    queryFn: async ({ signal }) => fetchApi(url, { signal }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** `url` is a 30-minute signed recording URL, or null when the call has none. */
export function useContactDetailAudio(url: string | undefined) {
  return useQuery<{ url: string | null }>({
    queryKey: ["contact-audio", url],
    queryFn: async ({ signal }) => fetchApi(url!, { signal }),
    enabled: !!url,
    // Under the signature's 30 minutes, so a cached URL still plays.
    staleTime: 20 * 60 * 1000,
  });
}

export function useContactDetailEmail(url: string, { enabled }: { enabled: boolean }) {
  return useQuery<ContactEmail>({
    queryKey: ["contact-email", url],
    queryFn: async ({ signal }) => fetchApi(url, { signal }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}
